import { useCallback, useState, useEffect } from 'react';
import styles from './edit-tax-setting-form.module.sass';
import Select from 'componets/select';
import Button from 'componets/button';
import classNames from 'classnames';
import { InfoCircleFillIcon } from 'icons';
import analytics from 'service/analytics';

/**
 * @typedef EnterpriseTaxSetting
 *
 * @property {string} id - id
 * @property {string} title - title
 * @property {string} taxIdNumber - taxIdNumber
 */

/**
 * ### EditTaxSettingForm
 *
 * @param {boolean} isEdit - is edit mode
 * @param {string} title - selected value
 * @param {string} description - description
 * @param {string} note - note
 * @param {string} className - custom class name
 * @param {EnterpriseTaxSetting} defaultTaxSetting - default tax setting
 * @param {Array<EnterpriseTaxSetting>} enterpriseTaxSettings
 * @param {function} onClose
 * @param {function} onSubmit
 * @param {string} errorMessage - error message
 * @param {boolean} isLoading
 *
 * @returns
 */

export const EditTaxSettingForm = ({
  isEdit = false,
  title,
  description,
  note,
  defaultTaxSetting = null,
  errorMessage: _errorMessage,
  enterpriseTaxSettings,
  className = '',
  onClose,
  onSubmit,
  isLoading,
}) => {
  const selectDataList = enterpriseTaxSettings.map(({ id, title, taxIdNumber }) => ({
    label: `${taxIdNumber} (${title}) `,
    value: id,
  }));

  const [value, setValue] = useState(defaultTaxSetting?.id ?? null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSelect = useCallback(
    _value => {
      if (isEdit) {
        analytics.licensePlatesEditTaxSettingSelect({ option: _value });
      } else {
        analytics.licensePlatesCreateTaxSettingSelect({ option: _value });
      }
      setValue(_value);
    },
    [isEdit]
  );

  // If there is only one tax setting, set the value to the only one
  useEffect(() => {
    if (selectDataList.length !== 1) return;
    setValue(selectDataList[0].value);
  }, [selectDataList]);

  return (
    <form
      className={classNames(styles['edit-tax-setting-form'], className)}
      onSubmit={e => {
        e.preventDefault();

        const selectedTaxSetting = enterpriseTaxSettings.find(x => x.id === value);

        if (selectedTaxSetting == null) {
          setErrorMessage('此欄位為必填');
          isEdit
            ? analytics.licensePlatesEditTaxSettingEmptyAlert()
            : analytics.licensePlatesCreateTaxSettingEmptyAlert();
          return;
        }

        onSubmit(selectedTaxSetting);
      }}
    >
      <div className={styles['title-section']}>
        <div className={styles['title']}>{title}</div>
        {description && <p className={styles['description']}>{description}</p>}
      </div>
      <div className={styles['tax-setting-section']}>
        <label>發票歸帳</label>
        <Select
          className={styles['select']}
          placeholder="選擇發票歸帳"
          data={selectDataList}
          value={value}
          errorMessage={_errorMessage ?? errorMessage}
          onSelect={handleSelect}
          onClear={() => {
            setValue(null);
            setErrorMessage(null);
          }}
        />
      </div>
      {note && (
        <div className={styles['note']}>
          <InfoCircleFillIcon style={{ minWidth: '20px', marginRight: '4px' }} width={20} height={20} />
          {note}
        </div>
      )}
      <div className={styles['button-section']}>
        {onClose && (
          <Button variant="text" type="button" onClick={onClose}>
            取消
          </Button>
        )}
        <Button loading={isLoading} type="submit">
          確認
        </Button>
      </div>
    </form>
  );
};
